import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTopButton from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PageMeta from '@/components/feature/PageMeta';
import { aboutData } from '@/mocks/about';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9pco1aqs38sj6jc7lug';

function ContactForm() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const formData = new FormData(form);
    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }
    formData.delete('website_alt');

    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      setFormStatus('error');
      setFormError('留言内容不能超过500个字符');
      return;
    }

    setFormStatus('loading');

    try {
      const body = new URLSearchParams();
      for (const [key, val] of formData.entries()) {
        body.append(key, val as string);
      }

      const res = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await res.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
            您的姓名 <span className="text-primary-500">*</span>
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
            placeholder="请输入您的姓名"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
            电子邮箱 <span className="text-primary-500">*</span>
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
            placeholder="请输入您的邮箱"
          />
        </div>
      </div>
      <div>
        <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
          主题
        </label>
        <input
          id="contact-subject"
          name="subject"
          type="text"
          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
          placeholder="请选择主题"
        />
      </div>
      <div>
        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
          留言内容 <span className="text-primary-500">*</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
          placeholder="请输入您的留言内容（500字以内）"
        ></textarea>
      </div>
      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />
      <button
        type="submit"
        disabled={formStatus === 'loading'}
        className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-background-50 font-semibold rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap"
      >
        {formStatus === 'loading' ? '发送中...' : '发送留言'}
      </button>
      {formStatus === 'success' && (
        <p className="text-sm text-green-600 text-center">留言发送成功，我们会尽快回复您！</p>
      )}
      {formStatus === 'error' && (
        <p className="text-sm text-red-500 text-center">{formError || '发送失败，请稍后重试'}</p>
      )}
    </form>
  );
}

export default function AboutPage() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于韩漫之家 - 免费韩国漫画在线阅读平台',
    description: aboutData.introduction,
    url: 'https://hanmanzhijia.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫之家',
      url: 'https://hanmanzhijia.com',
    },
    about: {
      '@type': 'Organization',
      name: aboutData.brandName,
      description: aboutData.introduction,
      email: aboutData.contact.email,
      telephone: aboutData.contact.phone,
      address: {
        '@type': 'PostalAddress',
        streetAddress: aboutData.contact.address,
      },
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '韩漫之家的漫画都是免费的吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，韩漫之家平台上的所有漫画均为免费阅读。我们致力于为华语读者提供免费的韩国漫画阅读体验。',
        },
      },
      {
        '@type': 'Question',
        name: '漫画的更新频率是怎样的？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们的专业翻译团队会与韩国官方同步更新，确保读者能在第一时间阅读到最新的漫画章节。热门漫画通常每周更新1-2次。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系韩漫之家？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过页面的联系表单、发送邮件至 contact@hanmanzhijia.com，或拨打客服电话 400-888-6789 与我们取得联系。',
        },
      },
      {
        '@type': 'Question',
        name: '韩漫之家支持哪些设备阅读？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫之家采用响应式设计，完美适配电脑、平板和手机等各种设备。无论您使用什么设备，都能获得流畅的漫画阅读体验。',
        },
      },
    ],
  };

  return (
    <>
      <PageMeta
        title="关于我们 - 韩漫之家 - 免费韩国漫画在线阅读平台"
        description={`${aboutData.introduction.substring(0, 120)}。韩漫之家，您的韩国漫画阅读乐园。`}
        keywords="韩漫之家,关于我们,免费韩国漫画,漫画平台,联系方式"
        canonicalUrl="https://hanmanzhijia.com/about"
        schema={{ ...schema, ...faqSchema }}
      />
      <ScrollToTopButton />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="py-4">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
          </div>

          {/* Brand Introduction */}
          <section className="py-10" aria-labelledby="about-title">
            <div className="text-center mb-12">
              <h1 id="about-title" className="text-3xl md:text-4xl font-bold font-heading text-foreground-950">
                关于<strong className="text-primary-600">韩漫之家</strong>
              </h1>
              <p className="mt-3 text-lg text-foreground-500 font-heading">{aboutData.tagline}</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-xl font-bold font-heading text-foreground-900 mb-4">品牌介绍</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutData.introduction}</p>
                <p className="mt-4 text-sm text-foreground-600 leading-relaxed">{aboutData.mission}</p>
              </div>
              <div className="rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20cozy%20modern%20office%20space%20with%20manga%20and%20comic%20books%20displayed%20on%20shelves%2C%20warm%20lighting%2C%20comfortable%20reading%20nook%20with%20armchairs%2C%20creative%20workspace%20with%20computer%20monitors%20showing%20digital%20art%2C%20plants%20decorating%20the%20space%2C%20inviting%20atmosphere%2C%20Asian%20creative%20studio%20aesthetic%2C%20soft%20natural%20light%20from%20large%20windows%2C%20professional%20editorial%20photography&width=1200&height=800&seq=about-brand-img&orientation=landscape"
                  alt="韩漫之家办公环境"
                  title="韩漫之家 - 专业的漫画阅读平台"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="py-10 border-t border-background-200" aria-labelledby="stats-title">
            <h2 id="stats-title" className="sr-only">平台数据</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {aboutData.stats.map(stat => (
                <div key={stat.label} className="text-center p-6 rounded-xl bg-background-100">
                  <div className="text-3xl md:text-4xl font-bold font-heading text-primary-600">
                    {stat.value}
                  </div>
                  <div className="mt-2 text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Our Advantages */}
          <section className="py-10 border-t border-background-200" aria-labelledby="advantages-title">
            <div className="text-center mb-10">
              <h2 id="advantages-title" className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                我们的优势
              </h2>
              <p className="mt-2 text-sm text-foreground-500">为什么数百万读者选择我们</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {aboutData.features.map(feat => (
                <div
                  key={feat.title}
                  className="flex gap-4 p-6 rounded-xl bg-background-50 border border-background-200/70"
                >
                  <div className="flex-shrink-0 w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className={`${feat.icon} text-lg w-5 h-5 flex items-center justify-center`}></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold font-heading text-foreground-900 mb-1.5">
                      {feat.title}
                    </h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{feat.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Testimonials */}
          <section className="py-10 border-t border-background-200" aria-labelledby="testimonials-title">
            <div className="text-center mb-10">
              <h2 id="testimonials-title" className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                用户口碑
              </h2>
              <p className="mt-2 text-sm text-foreground-500">听听读者们怎么说</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {aboutData.testimonials.map(testimonial => (
                <div
                  key={testimonial.name}
                  className="p-6 rounded-xl bg-background-50 border border-background-200/70 flex flex-col"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map(i => (
                      <i key={i} className="ri-star-fill text-accent-500 text-xs w-3 h-3 flex items-center justify-center"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed flex-1">
                    &ldquo;{testimonial.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3 mt-4 pt-4 border-t border-background-200/60">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={testimonial.avatar}
                        alt={testimonial.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-800">{testimonial.name}</p>
                      <p className="text-xs text-foreground-500">{testimonial.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="py-10 border-t border-background-200" aria-labelledby="faq-title">
            <div className="text-center mb-10">
              <h2 id="faq-title" className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                常见问题
              </h2>
              <p className="mt-2 text-sm text-foreground-500">关于韩漫之家的常见疑问</p>
            </div>
            <div className="max-w-2xl mx-auto space-y-4">
              {[
                { q: '韩漫之家的漫画都是免费的吗？', a: '是的，韩漫之家平台上的所有漫画均为免费阅读。我们致力于为华语读者提供免费的韩国漫画阅读体验。' },
                { q: '漫画的更新频率是怎样的？', a: '我们的专业翻译团队会与韩国官方同步更新，确保读者能在第一时间阅读到最新的漫画章节。热门漫画通常每周更新1-2次。' },
                { q: '如何联系韩漫之家？', a: '您可以通过页面的联系表单、发送邮件至 contact@hanmanzhijia.com，或拨打客服电话 400-888-6789 与我们取得联系。' },
                { q: '韩漫之家支持哪些设备阅读？', a: '韩漫之家采用响应式设计，完美适配电脑、平板和手机等各种设备。无论您使用什么设备，都能获得流畅的漫画阅读体验。' },
              ].map(faq => (
                <details
                  key={faq.q}
                  className="group p-5 rounded-xl bg-background-50 border border-background-200/70 cursor-pointer"
                >
                  <summary className="flex items-center justify-between text-sm font-semibold font-heading text-foreground-900 list-none">
                    {faq.q}
                    <i className="ri-arrow-down-s-line w-5 h-5 flex items-center justify-center text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                </details>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="py-10 border-t border-background-200" aria-labelledby="contact-title">
            <div className="text-center mb-10">
              <h2 id="contact-title" className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                联系我们
              </h2>
              <p className="mt-2 text-sm text-foreground-500">如有任何问题或建议，欢迎联系我们</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h3 className="text-base font-semibold font-heading text-foreground-900 mb-4">
                  发送留言
                </h3>
                <ContactForm />
              </div>
              <div className="space-y-5">
                <h3 className="text-base font-semibold font-heading text-foreground-900 mb-4">
                  联系方式
                </h3>
                <div className="space-y-4">
                  {[
                    { icon: 'ri-mail-line', label: '电子邮箱', value: aboutData.contact.email },
                    { icon: 'ri-phone-line', label: '客服电话', value: aboutData.contact.phone },
                    { icon: 'ri-wechat-line', label: '微信公众号', value: aboutData.contact.wechat },
                    { icon: 'ri-weibo-line', label: '官方微博', value: aboutData.contact.weibo },
                    { icon: 'ri-qq-line', label: 'QQ群', value: aboutData.contact.qqGroup },
                    { icon: 'ri-map-pin-line', label: '公司地址', value: aboutData.contact.address },
                  ].map(item => (
                    <div key={item.label} className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600">
                        <i className={`${item.icon} text-sm w-4 h-4 flex items-center justify-center`}></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">{item.label}</p>
                        <p className="text-sm font-medium text-foreground-800">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Back Nav */}
        <div className="py-8 text-center">
          <a href="/" className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-arrow-left-line mr-1 w-4 h-4 inline-flex items-center justify-center"></i>
            返回首页
          </a>
        </div>
      </main>

      <Footer />
    </>
  );
}